import * as yup from "yup";

import {
  OBJECTS_LIST_TILE_IMAGE_DRAGGABLE_TYPE,
  OBJECTS_LIST_TILE_VIDEO_DRAGGABLE_TYPE,
} from "@/components/objects-list/components/ObjectsListTile";
import { FILE_PICKER_CONFIG_COMMON } from "@/components/ui-v2/FilePicker";

import {
  ImageUploadField,
  ImageUploadFieldProps,
} from "../../components/ImageUploadField";
import { getFieldArrayErrorMessage } from "../../fieldArrayUtils";
import { FieldComponentProps, FileReferenceType } from "../../types";
import { getRequiredErrorMessage } from "../../validationMessages";
import { FIELD_NAMES } from "../constants";
import { FieldDef, FieldDefOverrides } from "../FieldDefs";

export type SourceImagesFieldValue = FileReferenceType[];

const DEFAULT_VALUE: SourceImagesFieldValue = [];

export type SourceImagesComponentProps = ImageUploadFieldProps;

export const MAX_EDIT_IMAGE_DIMENSION = 4096;

export const getIsEditImageTargetFileValid = (
  width?: number,
  height?: number,
) =>
  !!width &&
  !!height &&
  width <= MAX_EDIT_IMAGE_DIMENSION &&
  height <= MAX_EDIT_IMAGE_DIMENSION;

const SourceImagesField = (
  props: FieldComponentProps<SourceImagesFieldValue> & ImageUploadFieldProps,
) => {
  const { controller, ...rest } = props;
  return (
    <ImageUploadField
      {...rest}
      value={controller.field.value}
      onChange={controller.field.onChange}
      error={{
        message: getFieldArrayErrorMessage(controller.fieldState.error),
      }}
    />
  );
};

export const createSourceImagesFieldDef = (
  overrides?: FieldDefOverrides<"sourceImages">,
): FieldDef => ({
  name: FIELD_NAMES.sourceImages,
  Component: SourceImagesField,
  validationSchema: yup
    .array()
    .min(1, getRequiredErrorMessage("Image"))
    .of(
      yup
        .object()
        .shape({
          src: yup.string().required(),
          width: yup.number().optional(),
          height: yup.number().optional(),
        })
        .test(
          "image-size",
          `Please make sure your images are under ${MAX_EDIT_IMAGE_DIMENSION}x${MAX_EDIT_IMAGE_DIMENSION}.`,
          (value) => getIsEditImageTargetFileValid(value.width, value.height),
        ),
    ),
  defaultValue: DEFAULT_VALUE,
  ...overrides,
  emptyValue: DEFAULT_VALUE,
  componentProps: {
    label: "Image(s)",
    tooltip: "The image(s) you want to edit.",
    isDroppableEnabled: true,
    droppableAccepts: [
      OBJECTS_LIST_TILE_IMAGE_DRAGGABLE_TYPE,
      OBJECTS_LIST_TILE_VIDEO_DRAGGABLE_TYPE,
    ],
    droppableId: "sourceImages",
    isEditorEnabled: true,
    modality: "IMAGE",
    filePickerConfig: FILE_PICKER_CONFIG_COMMON,
    ...overrides?.componentProps,
  },
});
